import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import GoogleMap from 'google-map-react';
import { Box, IconButton } from '@mui/material';
import { findIndex, get } from 'lodash';
import { Add, Remove } from '@mui/icons-material';
import useNotificationService from '../../../hooks/useNotificationService';
import useActiveSite from '../../../hooks/useActiveSite';
import { getMap } from '../../../api/map';
import { getColorByIndex } from '../../../helpers/colors';
import { getRescuePickupLocationFullName } from '../../../helpers/RescuesHelper';
import { MARKER_DONOR, MARKER_RECEIVER } from '../../Map/Components/MarkersSelector';
import { renderLocationInfoWindow } from '../../Map/MapInfoWindows';
import { formatAddress } from '../../../helpers/formatters';
import { getUserFullName } from '../../../helpers/user';
import { Colors } from '../../../assets/theme/Colors';
import Legend from './Legend';

const createMarkerIcon = (color, maps) => ({
  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
        <circle cx="16" cy="16" r="10" fill="${color}" stroke="#fff" stroke-width="2" />
        <circle cx="16" cy="16" r="6" fill="#fff" />
      </svg>
    `)}`,
  scaledSize: new maps.Size(32, 32),
  anchor: new maps.Point(16, 16),
});

const RoutesMap = ({
  addLocation = () => {},
  handleLocationSelect = () => {},
  updateDistanceAndDuration = () => {},
  showDonors = false,
  showReceivers = false,
  routes = [],
  allRoutes = [],
  locations = [],
  selectedLocations = [],
  newRouteStep = false,
  stopDetailsStep = false,
  reviewStep = false,
  styles = {},
}) => {
  const activeSite = useActiveSite();
  const mapApiRef = useRef();
  const [mapApi, setMapApi] = useState();
  const directionRef = useRef([]);
  const stopMarkersRef = useRef([]);
  const [markers, setMarkers] = useState([]);
  const { addErrorNotification } = useNotificationService();

  const renderMarkers = (type = false) => {
    if (!mapApi) {
      return;
    }

    const { map, maps } = mapApi;
    let markersToRender;

    if (type) {
      const filteredMarkers = locations.filter((marker) => marker.type === type);
      markersToRender = filteredMarkers;
    } else {
      markersToRender = locations;
    }

    const bounds = new maps.LatLngBounds();

    markersToRender.forEach((location) => {
      const infoWindow = new maps.InfoWindow({
        content: null,
      });

      const marker = new maps.Marker({
        position: {
          lat: location.lat,
          lng: location.lng,
        },
        map,
        icon: createMarkerIcon(location.type === MARKER_DONOR ? Colors.primaryColor : Colors.appBar.main, maps),
        meta: location,
      });

      marker.addListener('mouseover', () => {
        const div = document.createElement('div');
        const root = ReactDOM.createRoot(div);

        switch (location.type) {
          case MARKER_RECEIVER:
            root.render(
              renderLocationInfoWindow({
                text: location.name,
                address: formatAddress({
                  address: location.address,
                  city: location.city,
                  st: location.state,
                  zip: location.zip_code,
                }),
                contact: {
                  email: location.primary_contact_email,
                  phone: get(location, 'primary_contact.phone'),
                  name: location.primary_contact ? getUserFullName(location.primary_contact) : null,
                },
              })
            );
            break;
          case MARKER_DONOR:
            root.render(
              renderLocationInfoWindow({
                text: location.title,
                address: formatAddress({
                  address: location.address,
                  city: location.city,
                  st: location.state,
                  zip: location.zip_code,
                }),
                contact: {
                  email: location.primary_contact_email,
                  phone: get(location, 'primary_contact.phone'),
                  name: location.primary_contact ? getUserFullName(location.primary_contact) : null,
                },
              })
            );
            break;

          default:
        }

        div.addEventListener('mouseleave', () => {
          infoWindow.close();
        });

        infoWindow.setContent(div);
        infoWindow.open(map, marker);
      });

      marker.addListener('mouseout', () => {
        infoWindow.close();
      });

      marker.addListener('click', () => handleLocationSelect(location));
      bounds.extend(marker.getPosition());
      setMarkers((prev) => [...prev, marker]);
    });

    if (!selectedLocations.length) {
      map.fitBounds(bounds);
    }
  };

  const clearRoutes = () => {
    directionRef.current.forEach((r) => r.setMap(null));
    directionRef.current = [];
  };

  const clearStopMarkers = () => {
    stopMarkersRef.current.forEach((r) => r.setMap(null));
    stopMarkersRef.current = [];
  };

  const renderRoute = (routePoints = [], color = '', bounds) => {
    if (!routePoints.length) {
      return Promise.resolve();
    }

    const { map, maps } = mapApi;
    const directionsService = new maps.DirectionsService();

    const renderer = new maps.DirectionsRenderer();
    renderer.setOptions({
      suppressMarkers: true,
      preserveViewport: true,
      polylineOptions: {
        strokeColor: color,
        strokeWeight: 6,
      },
    });
    renderer.setMap(map);

    directionRef.current.push(renderer);

    const route = {
      origin: routePoints[0],
      destination: routePoints[routePoints.length - 1],
      waypoints: routePoints.slice(1, -1).map((point) => ({
        location: point,
        stopover: true,
      })),
      travelMode: 'DRIVING',
    };

    return new Promise((resolve) => {
      directionsService.route(route, (response, status) => {
        if (status !== 'OK') {
          addErrorNotification(`Directions request failed due to ${status}`);
          resolve();
        } else {
          // render route
          renderer.setDirections(response);

          // render markers
          const stopMarkers = routePoints.map(
            (location, index) =>
              new maps.Marker({
                position: location,
                map,
                zIndex: (newRouteStep || stopDetailsStep) ? 999 : 10,
                icon: {
                  path: maps.SymbolPath.CIRCLE,
                  scale: 12,
                  fillColor:
                    location.type === MARKER_RECEIVER && (newRouteStep || stopDetailsStep) ? Colors.appBar.main : color,
                  fillOpacity: 1,
                  strokeColor: '#fff',
                  strokeWeight: 2,
                },
                label: {
                  text: String(index + 1),
                  color: '#fff',
                  fontSize: '11px',
                  fontWeight: 'bold',
                },
              })
          );

          stopMarkersRef.current.push(...stopMarkers);

          if (newRouteStep) {
            const legs = response.routes[0]?.legs;
            if (legs?.length) {
              const totalDistanceMeters = legs.reduce((sum, leg) => sum + leg.distance.value, 0);
              const totalDurationSeconds = legs.reduce((sum, leg) => sum + leg.duration.value, 0);
              updateDistanceAndDuration({ distance: totalDistanceMeters, duration: totalDurationSeconds });
            }
          }

          // center map
          if (bounds) {
            bounds.union(response.routes[0].bounds);

            if (newRouteStep || stopDetailsStep) {
              map.setCenter(bounds.getCenter());
              map.fitBounds(bounds);

              if (selectedLocations.length === 1) {
                map.setZoom(map.getZoom() - 6);
              }
            }

            resolve();
          }
        }
      });
    });
  };

  useEffect(() => {
    if (stopDetailsStep) {
      return;
    }

    if (!mapApi) {
      return;
    }

    const { map } = mapApi;

    if (activeSite?.id) {
      getMap([activeSite.id])
        .then((res) => res.json())
        .then((result) => result.data)
        .then((sitesData) => {
          for (const siteData of sitesData) {
            for (const foodDonorData of siteData.food_donors) {
              for (const donorPickupLocation of foodDonorData.pickup_locations) {
                if (donorPickupLocation.lat && donorPickupLocation.long) {
                  const pickupLocationFullName = getRescuePickupLocationFullName({
                    pickup_location_name: donorPickupLocation.name,
                    location: foodDonorData.name,
                  });

                  addLocation({
                    id: `donor_pl_${donorPickupLocation.id}`,
                    lat: donorPickupLocation.lat,
                    lng: donorPickupLocation.long,
                    type: MARKER_DONOR,
                    title: pickupLocationFullName,
                    site_id: siteData.id,
                    site_name: siteData.name,
                    donor_id: foodDonorData.id,
                    donor_name: foodDonorData.name,
                    ...donorPickupLocation,
                  });
                }
              }
            }

            for (const receiverData of siteData.receivers) {
              for (const receiverDataLocation of receiverData.receiver_locations) {
                if (receiverDataLocation.lat && receiverDataLocation.long) {
                  addLocation({
                    id: `receiver_pl_${receiverDataLocation.id}`,
                    lat: receiverDataLocation.lat,
                    lng: receiverDataLocation.long,
                    type: MARKER_RECEIVER,
                    site_id: siteData.id,
                    site_name: siteData.name,
                    receiver_id: receiverData.id,
                    ...receiverDataLocation,
                  });
                }
              }
            }
          }

          const siteZipCodes = sitesData[0]?.zip_codes;
          if (siteZipCodes.length) {
            const siteLat = siteZipCodes[0]?.lat;
            const siteLong = siteZipCodes[0]?.long;
            map.setCenter({ lat: siteLat, lng: siteLong });
            map.setZoom(9);
          }
        });
    }
  }, [activeSite, mapApi]);

  useEffect(() => {
    if (!mapApi) {
      return;
    }

    if (newRouteStep || stopDetailsStep) {
      return;
    }

    clearRoutes();
    clearStopMarkers();

    const { maps, map } = mapApi;
    const bounds = new maps.LatLngBounds();
    const routesToRender = routes.filter((_, index) => index <= 3); // render only first 4 routes

    const routePromises = routesToRender.map((route) => {
      const routePoints = route.destinations.map((d) => ({ lat: d.lat, lng: d.long }));
      const allRoutesIndex = findIndex(allRoutes, (ar) => ar.id === route.id);
      const routeColor = allRoutesIndex >= 0 ? getColorByIndex(allRoutesIndex) : Colors.primaryColor;
      return renderRoute(routePoints, routeColor, bounds);
    });

    Promise.all(routePromises).then(() => {
      if (!bounds.isEmpty()) {
        map.setCenter(bounds.getCenter());
        map.fitBounds(bounds);
      }
    });
  }, [mapApi, routes, newRouteStep]);

  useEffect(() => {
    markers.forEach((marker) => {
      marker.setMap(null);
    });

    if (newRouteStep || stopDetailsStep) {
      const type = showDonors ? MARKER_DONOR : MARKER_RECEIVER;
      renderMarkers(type);
    }
  }, [newRouteStep, stopDetailsStep, showDonors, showReceivers, mapApi]);

  useEffect(() => {
    if (!mapApi) {
      return;
    }
    if (newRouteStep || stopDetailsStep) {
      clearRoutes();
      clearStopMarkers();

      if (selectedLocations.length) {
        const { maps } = mapApi;
        const bounds = new maps.LatLngBounds();
        const routePoints = selectedLocations.map((d) => ({ lat: d.lat, lng: d.long, type: d.type }));

        renderRoute(routePoints, Colors.primaryColor, bounds);
      }
    }
  }, [selectedLocations, newRouteStep, stopDetailsStep, mapApi]);

  useEffect(() => {
    if (!mapApi) {
      return;
    }

    if (reviewStep) {
      const { maps, map } = mapApi;
      const bounds = new maps.LatLngBounds();

      selectedLocations.forEach((loc) => {
        bounds.extend(new maps.LatLng(loc.lat, loc.long));
      });

      map.fitBounds(bounds);
    }
  }, [reviewStep]);

  const defaultProps = {
    center: {
      lat: 39.0915821,
      lng: -94.8565884,
    },
    defaultZoom: 5,
    minZoom: 5,
    maxZoom: 18,
  };

  return (
    <Box sx={styles}>
      <GoogleMap
        ref={mapApiRef}
        bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_MAP_API_KEY }}
        defaultCenter={defaultProps.center}
        defaultZoom={defaultProps.defaultZoom}
        hoverDistance={20}
        options={{
          fullscreenControl: false,
          zoomControl: false,
          maxZoom: defaultProps.maxZoom,
          minZoom: defaultProps.minZoom,
        }}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={(api) => setMapApi(api)}
      />

      <Box
        boxShadow={25}
        bgcolor="background.paper"
        sx={{
          padding: 1,
          position: 'absolute',
          bottom: 20,
          right: 10,
          zIndex: 5,
          display: 'grid',
          gridAutoFlow: 'row',
          rowGap: '16px',
        }}
      >
        <IconButton size="small" onClick={() => mapApi.map.setZoom(mapApi.map.getZoom() + 1)}>
          <Add />
        </IconButton>
        <IconButton size="small" onClick={() => mapApi.map.setZoom(mapApi.map.getZoom() - 1)}>
          <Remove />
        </IconButton>
      </Box>

      {newRouteStep && <Legend />}
    </Box>
  );
};

export default RoutesMap;
